import { DateTime } from 'luxon';

import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

import { joinIds } from '@/clientOnlyUtils/clientUtils';

/**
 * One signed term of the smart-order score — a label and the points it contributes. The values of a
 * call's terms sum to its `score`, so the tooltip can show how the number was assembled. Structurally
 * matches the server's `ScoreLineItem`, so queue rows pass straight through.
 */
export interface ScoreTerm {
  label: string;
  value: number;
}

/**
 * One upcoming call to preview as a face. Structurally a subset of `CallQueueRow`, so the calls
 * page can pass its queue rows straight through without reshaping.
 */
export interface UpcomingCall {
  reminderId: string;
  /** contact_public_id — used to deep-link the face to the contact's detail panel (opens in a new tab). */
  contactId: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactImageUrl: string | null;
  /**
   * The contact's background-removed cutout (trimmed → no-bg), preferred for the face here because a
   * clean head-and-shoulders sticker reads better in a small circle. Null → fall back to
   * `contactImageUrl` (the raw photo).
   */
  contactCutoutImageUrl: string | null;
  /** Smart-order score (higher = called sooner). Surfaced in the tooltip so the ordering is visible. */
  score: number;
  /** Human-readable reasons the score fired ("First touch", "Great local time", …). */
  reasons: string[];
  /** Per-term breakdown of `score` ({ label, value }); the values sum to `score`. Explains the number. */
  scoreBreakdown: ScoreTerm[];
  /**
   * The effective IANA timezone the score was computed against (the contact's own, else the Pacific
   * default) — drives the "current local time" caption under the face, so the rep can see at a glance
   * it's the middle of the night for a dimmed, deferred call. Null/invalid → no time shown.
   */
  contactTimezone: string | null;
  /**
   * The contact's city / location — shown under their local time in the hover popup so the rep can
   * place them geographically (why it's that time for them). Null/empty → no location line.
   */
  contactCity: string | null;
}

export interface UpcomingCallsStripProps {
  /** The queued calls AFTER the current one, already in call order (next up first). */
  calls: UpcomingCall[];
  /**
   * Queue position of the first face. Defaults to 2 (the current call is #1, shown above). When
   * there's no active call — every remaining call is outside business hours — pass 1 so the preview
   * numbers from the top.
   */
  startPosition?: number;
  className?: string;
}

const fullName = (c: UpcomingCall) =>
  [c.contactFirstName, c.contactLastName].filter((v) => v != null && v !== '').join(' ') || 'Unknown contact';

/**
 * New-tab deep link to a contact's detail panel. Points at `/leads` (whose `getDetailPage` renders the
 * contact detail) with the same `id` + `detailType` a list click would set, so the global panel opens
 * for this contact regardless of what the leads list is currently showing.
 */
export const contactDetailHref = (contactId: string) =>
  `/leads?${new URLSearchParams({ id: joinIds({ contactId }), detailType: 'interact' }).toString()}`;

/**
 * The contact's CURRENT wall-clock time in their own timezone, e.g. "2:45 PM" — computed fresh on
 * each render (the queue refetches every few seconds, so it stays current). Returns null when the
 * timezone is unknown or invalid, so the UI shows a time only when we actually know it.
 */
export const localTimeLabel = (timezone: string | null): string | null => {
  if (timezone == null || timezone === '') return null;
  const dt = DateTime.now().setZone(timezone);
  return dt.isValid ? dt.toFormat('h:mm a') : null;
};

/** A score term/total for the tooltip: rounded, thousands-separated, with an explicit `+` for gains. */
const fmtSigned = (n: number) => {
  const rounded = Math.round(n);
  // `Math.round(-0.5)` is `-0`, and `(-0).toLocaleString()` renders the literal "-0"; collapse anything
  // that rounds to zero (incl. sub-minute callback offsets) to a plain "0".
  if (rounded === 0) return '0';
  return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('en-US')}`;
};

/**
 * A row of faces at the bottom of `/todo/calls` previewing who's up next. Hovering a face shows why
 * the smart order placed them there — their queue position and an itemized score breakdown (every
 * term that fired with its signed points, summing to the total) — so the rep can see exactly how the
 * ordering number was assembled. The current call is shown separately above; these are the calls that
 * follow it, so the first face is queue position #2.
 */
export function UpcomingCallsStrip({ calls, startPosition = 2, className }: UpcomingCallsStripProps) {
  if (calls.length === 0) return null;
  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      {/* items-start so the faces stay top-aligned even when only some carry a local-time caption. */}
      <div className="flex items-start gap-3">
        {calls.map((c, i) => {
          const localTime = localTimeLabel(c.contactTimezone);
          // A background-removed cutout renders as a bare sticker (no plate, no rounded frame) that
          // peeks above the strip's top line; a raw photo keeps the framed, rounded look.
          const cutout = c.contactCutoutImageUrl;
          const hasCutout = cutout != null;
          return (
            <Tooltip
              key={c.reminderId}
              data-test="upcoming-call"
              tooltipPosition="top"
              tooltipMaxWidth={280}
              className="inline-flex flex-col items-center gap-1"
              tooltip={
                <div className="flex flex-col gap-1 text-left">
                  <div className="font-semibold">{fullName(c)}</div>
                  {/* Their current local time — sits next to the "Outside business hours" term below so
                      it's obvious why a deferred call is deferred (e.g. it's 3:20 AM for them). */}
                  {localTime != null && <div className="text-xs opacity-80">Local time · {localTime}</div>}
                  {/* Where they are — placed right under the local time so the rep can tie the time to a
                      location ("it's 8:57 AM in Denver"). */}
                  {c.contactCity != null && c.contactCity !== '' && (
                    <div className="text-xs opacity-80">{c.contactCity}</div>
                  )}
                  <div className="text-xs opacity-80">
                    #{i + startPosition} in queue
                    {/* No breakdown (e.g. the unscored 'due' order) → fall back to the bare score. */}
                    {c.scoreBreakdown.length === 0 && <> · score {fmtSigned(c.score)}</>}
                  </div>
                  {/* How the score got there: every term that fired, each with its signed points, then
                      the total. The term values add up to the Score row, so the math is checkable. */}
                  {c.scoreBreakdown.length > 0 && (
                    <div className="mt-0.5 flex flex-col gap-0.5">
                      {c.scoreBreakdown.map((t) => (
                        <div key={t.label} className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="min-w-0 opacity-80">{t.label}</span>
                          <span className={cn('shrink-0 tabular-nums', t.value < 0 ? 'text-red-300' : 'opacity-80')}>
                            {fmtSigned(t.value)}
                          </span>
                        </div>
                      ))}
                      <div className="mt-0.5 flex items-baseline justify-between gap-3 border-t border-white/20 pt-0.5 text-xs font-semibold">
                        <span>Score</span>
                        <span className="shrink-0 tabular-nums">{fmtSigned(c.score)}</span>
                      </div>
                    </div>
                  )}
                </div>
              }>
              {/* Clicking a face opens that contact's detail panel in a new tab, so the rep can dig into
                  who's next without losing their place in the queue. */}
              <a href={contactDetailHref(c.contactId)} target="_blank" rel="noopener noreferrer" className="inline-flex">
                <ContactAvatar
                  firstName={c.contactFirstName}
                  lastName={c.contactLastName}
                  imageSrc={cutout ?? c.contactImageUrl}
                  shape="square"
                  // Contain (not cover) for a cutout so the whole head shows instead of being cropped.
                  objectFit={hasCutout ? 'contain' : 'cover'}
                  className={cn(
                    'size-15 cursor-pointer transition-transform hover:scale-110',
                    hasCutout
                      ? // Transparent cutout: kill Avatar's plate + rounding, and lift it so 25% of its
                        // height rises above the strip's top line (a sticker peeking over the divider).
                        '-mt-[15px] [&>div]:rounded-none! [&>div]:bg-transparent!'
                      : // Raw photo: keep the framed look — rounded plate + subtle ring.
                        'rounded-md ring-2 ring-white dark:ring-gray-800',
                    // Deferred (negative score, e.g. outside the contact's business hours): dim + desaturate
                    // so it reads at a glance as "not callable right now" without hiding it.
                    c.score < 0 && 'opacity-50 grayscale',
                  )}
                />
              </a>
              {/* Current local time under the face. Amber when the call is deferred (score < 0 —
                  typically outside the contact's business hours) to reinforce "not now". */}
              {localTime != null && (
                <span
                  data-test="upcoming-call-local-time"
                  className={cn(
                    'text-2xs leading-none tabular-nums text-gray-500 dark:text-gray-400',
                    c.score < 0 && 'text-amber-600 dark:text-amber-400',
                  )}>
                  {localTime}
                </span>
              )}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
